import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Heading2 } from '@myphx-engineering/phx-design-system';
import {
    isGovernmentOfferType,
    isInsuranceOfferType,
    isSelfPayOfferType,
} from '@phx/data-sources/medication-marketplace';

import { getFragment } from '../../../../graphql/client/helpers/get-fragment';
import type {
    AuthorizedMedicationInfoFragment,
    CashOfferInfoFragment,
    InsuranceOfferInfoFragment,
    NonPriceableInsuranceOfferInfoFragment,
    NonPriceableOfferInfoFragment,
    UnknownMedicationInfoFragment,
} from '../../../../graphql/generated/graphql';
import type { HeadlinePrice } from '../../../../hooks/use-config-api';

import { FallbackPricing } from './FallbackPricing';
import { GovernmentPricing } from './GovernmentPricing';
import { InsurancePricing } from './InsurancePricing';
import { SelfPayPricing } from './SelfPayPricing';

export type OrderPricingProps = {
    medication:
        | AuthorizedMedicationInfoFragment
        | UnknownMedicationInfoFragment;
    selectedOffer:
        | CashOfferInfoFragment
        | InsuranceOfferInfoFragment
        | NonPriceableInsuranceOfferInfoFragment
        | NonPriceableOfferInfoFragment
        | undefined;
};

export const OrderPricing: FC<OrderPricingProps> = ({
    medication,
    selectedOffer,
}) => {
    const { t } = useTranslation('lilly');

    const isNonPriceable =
        selectedOffer?.__typename === 'NonPriceableOffer' ||
        selectedOffer?.__typename === 'NonPriceableInsuranceOffer';

    if (
        !selectedOffer ||
        (!isNonPriceable &&
            selectedOffer.__typename !== 'CashOffer' &&
            selectedOffer.__typename !== 'InsuranceOffer')
    ) {
        return null;
    }

    const headlinePriceType = isNonPriceable
        ? null
        : (selectedOffer as CashOfferInfoFragment | InsuranceOfferInfoFragment)
              .headlinePriceType;
    const patientResponsibilityCost = isNonPriceable
        ? null
        : (selectedOffer as CashOfferInfoFragment | InsuranceOfferInfoFragment)
              .patientResponsibilityCost;

    const priceType = headlinePriceType
        ? (headlinePriceType as HeadlinePrice['type'])
        : null;

    const provider = getFragment(selectedOffer.pharmacyProvider);

    // oxlint-disable-next-line react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md
    const PricingComponent = useMemo(() => {
        if (isInsuranceOfferType(priceType)) {
            return InsurancePricing;
        }

        if (isSelfPayOfferType(priceType)) {
            return SelfPayPricing;
        }

        if (isGovernmentOfferType(priceType)) {
            return GovernmentPricing;
        }

        return FallbackPricing;
    }, [priceType]);

    return (
        <>
            <Heading2>{t('orderConfirmation.pricing.header')}</Heading2>
            <PricingComponent
                medication={medication}
                price={patientResponsibilityCost}
                priceType={priceType}
                provider={provider}
                selectedOffer={selectedOffer}
            />
        </>
    );
};
